import {
  IResetAccountPassword,
  IChangeTemporaryPassword,
  type IAccountRequestState,
  type IAccountStatus,
  type ICheckResetPasswordCode,
  type IConfirmEmailAccount,
  type ICreateAccount,
  type ICreateAccountPassword,
  type IEmailAccountStatus,
  type IResendEmailAccountConfirmation,
  type ISendEmailResetPassword,
  TOperationsPeriodsDescriptions,
  TCountryCodePhoneNumber,
  INewPassword,
  IFavoriteMenu,
} from './entities/account.interface';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap, Observable } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { Encrypt } from '@shared/utils';
import { AccountService } from './infra/account.service';
import { RouterStore } from '@route/data-access';
import { AuthStore } from './auth.store';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { adminTenants } from '@application/config';
import { LocalStorageFacade } from '@application/localstorage';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import {
  IUserRole,
  IUser,
  PasswordDefinitionMode,
} from '@person/user/data-access';
import { UserService } from '@person/user/data-access';
import { PersonService } from '@person/register/data-access';
import { APP_CONFIG, AppSource } from '@application/config';
import { catchError, of } from 'rxjs';

interface IAccountStoreState {
  emailCheckedStatus: IEmailAccountStatus;
  createAccountEmail: string;
  emailToLogin: string;
  emailToRemember: string;
  emailToResetPassword: string;
  hasEmailToRemember: boolean;
  emailForgot: string;
  confirmEmailAndCreatePasswordResponse: any;
  emailSendedToResetPassword: IAccountRequestState;
  resetPasswordData: IAccountRequestState;
  checkResetPassword: ICheckResetPasswordCode;
  currentEmailChecking: string;
  tempCredentials: ICreateAccountPassword;
  operationsPeriodsDescriptions: TOperationsPeriodsDescriptions[];
  showMyAccount: boolean;
  countryCodePhoneNumber: TCountryCodePhoneNumber;
  activeAccount: Partial<IUserRole>;
  routeToRedirectAfterAccountSwitch?: string;
}

export const AccountStoreInitial: IAccountStoreState = {
  emailCheckedStatus: null,
  createAccountEmail: null,
  emailToLogin: null,
  resetPasswordData: null,
  emailToRemember: null,
  emailForgot: null,
  emailToResetPassword: null,
  confirmEmailAndCreatePasswordResponse: null,
  hasEmailToRemember: false,
  emailSendedToResetPassword: null,
  checkResetPassword: null,
  currentEmailChecking: null,
  tempCredentials: null,
  operationsPeriodsDescriptions: null,
  showMyAccount: false,
  countryCodePhoneNumber: null,
  activeAccount: null,
  routeToRedirectAfterAccountSwitch: null,
};

export const AccountStore = signalStore(
  { providedIn: 'root' },
  withState(AccountStoreInitial),
  withComputed((state) => ({
    getEmailtoRemember: computed(() => state.emailToRemember()),
    getEmailToLogin: computed(() => state.emailToLogin()),
    getEmailSendedToResetPassword: computed(() =>
      state.emailSendedToResetPassword()
    ),
    getResetPassword: computed(() => state.resetPasswordData()),
    getAccountStatus: computed<IEmailAccountStatus>(() =>
      state.emailCheckedStatus()
    ),
    getTempLoginPayload: computed(() => state.emailToLogin()),
  })),
  withMethods(
    (
      state,
      accountService = inject(AccountService),
      routerStore = inject(RouterStore),
      crypt = inject(Encrypt),
      authStore = inject(AuthStore),
      storage = inject(LocalStorageFacade),
      errorStore = inject(ErrorStore)
    ) => {
      return {
        toggleMyAccount: (isOpened?: boolean) => {
          patchState(state, {
            showMyAccount: isOpened ?? !state.showMyAccount(),
          });
        },

        createAccount: rxMethod<Partial<ICreateAccount>>(
          pipe(
            map((payload) => {
              patchState(state, { ...setPropLoading('createAccount') });
              return payload;
            }),
            switchMap((payload) =>
              accountService.createAccount(payload).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, {
                      createAccountEmail: response.Email,
                      ...setPropLoaded('createAccount'),
                    });
                    routerStore.navigate([
                      '/common/account/register/confirm-email',
                    ]);
                  },
                  error: (err: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('createAccount', err),
                    });
                  },
                })
              )
            )
          )
        ),
        checkAccountStatus: rxMethod<string>(
          pipe(
            tap((currentEmailChecking) =>
              patchState(state, {
                currentEmailChecking,
                ...setPropLoading('checkAccountStatus'),
              })
            ),
            switchMap((Email) =>
              accountService.checkAccountStatus(Email).pipe(
                tapResponse({
                  next: (result: IAccountStatus) => {
                    const emailResult: IEmailAccountStatus = {
                      ...result,
                      Email,
                      Exist: !!result,
                    };
                    patchState(state, {
                      emailCheckedStatus: emailResult,
                      ...setPropLoaded('checkAccountStatus'),
                    });
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('checkAccountStatus', e),
                    });
                  },
                })
              )
            )
          )
        ),
        confirmEmailAndCreatePassword: rxMethod<Partial<INewPassword>>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('confirmEmailAndCreatePassword'),
              })
            ),
            map((confirmEmailAndCreatePassword) => {
              confirmEmailAndCreatePassword.Password = crypt.encryptMd5(
                confirmEmailAndCreatePassword.Email +
                  confirmEmailAndCreatePassword.Password
              );
              return confirmEmailAndCreatePassword;
            }),
            switchMap((payload) =>
              accountService.confirmEmailAndCreatePassword(payload).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, {
                      confirmEmailAndCreatePasswordResponse: response,
                      ...setPropLoaded('confirmEmailAndCreatePassword'),
                    });
                  },
                  error: (e: HttpErrorResponse) => {
                    errorStore.setHttpError(e);
                    patchState(state, {
                      ...setPropError('confirmEmailAndCreatePassword', e),
                    });
                  },
                })
              )
            )
          )
        ),
        confirmEmailAccount: rxMethod<IConfirmEmailAccount>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('confirmEmailAccount') })
            ),
            switchMap((code) =>
              accountService.confirmEmail(code).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, {
                      createAccountEmail: code.Email,
                      ...setPropLoaded('confirmEmailAccount'),
                    });
                    routerStore.navigate(['register/register-password']);
                  },
                  error: (e) => {
                    patchState(state, {
                      ...setPropError('confirmEmailAccount', e),
                    });
                  },
                })
              )
            )
          )
        ),
        createAccountPassword: rxMethod<ICreateAccountPassword>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('createAccountPassword') })
            ),
            map((createAccountPasswordForm) => {
              createAccountPasswordForm.Password = crypt.encryptMd5(
                createAccountPasswordForm.Email +
                  createAccountPasswordForm.Password
              );
              return createAccountPasswordForm;
            }),
            switchMap((payload) =>
              accountService.createAccountPassword(payload).pipe(
                tapResponse({
                  next: () => {
                    authStore.setTempLoginPayload({
                      Email: payload.Email,
                      Password: payload.Password,
                    });
                    patchState(state, {
                      ...setPropLoaded('createAccountPassword'),
                    });
                  },
                  error: (e: HttpErrorResponse) => {
                    errorStore.setHttpError(e);
                    patchState(state, {
                      ...setPropError('createAccountPassword', e),
                    });
                  },
                })
              )
            )
          )
        ),
        setEmailToRegister: (email: string) => {
          patchState(state, { createAccountEmail: email });
        },

        setEmailToLogin: (email: string) => {
          patchState(state, { emailToLogin: email });
        },
        clearForgotEmail: () => patchState(state, { emailForgot: null }),
        checkResetPasswordCode: rxMethod<ICheckResetPasswordCode>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('checkResetPasswordCode') })
            ),
            switchMap((payload) =>
              accountService.checkResetPasswordCode(payload).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, {
                      checkResetPassword: payload,
                      ...setPropLoaded('checkResetPasswordCode'),
                    });
                    routerStore.navigate(['/recover/register-password']);
                  },
                  error: (error) => {
                    patchState(state, {
                      ...setPropError('checkResetPasswordCode', error),
                    });
                  },
                })
              )
            )
          )
        ),
        resetPassword: rxMethod<IResetAccountPassword>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('resetPassword') })
            ),
            map((param) => {
              param.Password = crypt.encryptMd5(param.Email + param.Password);
              return param;
            }),
            switchMap((payload) =>
              accountService.resetPassword(payload).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, { ...setPropLoaded('resetPassword') });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(state, {
                      ...setPropError('resetPassword', error),
                    });
                  },
                })
              )
            )
          )
        ),
        changeTemporaryPassword: rxMethod<IChangeTemporaryPassword>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('changeTemporaryPassword'),
              })
            ),
            map((param) => {
              // Encrypt passwords following the same pattern as login: MD5(email.toLowerCase() + password)
              const normalizedEmail = param.Email.toLowerCase();
              const isMd5Hash = (value: string) =>
                /^[a-f0-9]{32}$/i.test(value);
              if (
                param.TemporaryPassword &&
                !isMd5Hash(param.TemporaryPassword)
              ) {
                param.TemporaryPassword = crypt.encryptMd5(
                  normalizedEmail + param.TemporaryPassword
                );
              }
              param.NewPassword = crypt.encryptMd5(
                normalizedEmail + param.NewPassword
              );
              return param;
            }),
            switchMap((payload) =>
              accountService.changeTemporaryPassword(payload).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, {
                      ...setPropLoaded('changeTemporaryPassword'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(state, {
                      ...setPropError('changeTemporaryPassword', error),
                    });
                  },
                })
              )
            )
          )
        ),
        getOperationsPeriodsDescriptions: rxMethod<void>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('getOperationsPeriodsDescriptions'),
              })
            ),
            switchMap(() =>
              accountService.getOperationsPeriodsDescriptions().pipe(
                tapResponse({
                  next: (operationsPeriodsDescriptions) => {
                    patchState(state, {
                      operationsPeriodsDescriptions,
                      ...setPropLoaded('getOperationsPeriodsDescriptions'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError(
                        'getOperationsPeriodsDescriptions',
                        error
                      ),
                    });
                  },
                })
              )
            )
          )
        ),
        checkCountryCodePhoneNumber: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('checkCountryCodePhoneNumber'),
              })
            ),
            switchMap((phoneNumber) =>
              accountService.checkCountryCodePhoneNumber(phoneNumber).pipe(
                tapResponse({
                  next: (countryCodePhoneNumber) => {
                    patchState(state, {
                      countryCodePhoneNumber,
                      ...setPropLoaded('checkCountryCodePhoneNumber'),
                    });
                  },
                  error: (e: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('checkCountryCodePhoneNumber', e),
                    });
                  },
                })
              )
            )
          )
        ),
        isAdminTenant: (): boolean => {
          const { TenantId } = JSON.parse(storage.getItem('authorization'));

          if (!TenantId) return false;

          return adminTenants.findIndex(
            (tenant) => tenant.account === TenantId
          ) < 0
            ? false
            : true;
        },
        blankState: () => {
          patchState(state, AccountStoreInitial);
        },
        setPropsInit(props: string[]): void {
          patchState(state, setPropInit(...props));
        },
        resetReqState: () => {
          patchState(state, {
            ...setPropInit(
              'createAccount',
              'resendCode',
              'resendEmailCodeConfirmAccount',
              'checkAccountStatus',
              'createAccountPassword',
              'sendEmailForgotPassword',
              'resendEmailForgotPassword',
              'checkResetPasswordCode',
              'resetPassword',
              'resendEmailLinkConfirmation',
              'getOperationsPeriodsDescriptions'
            ),
          });
        },

        animateAccountSwitch: (
          personId: string,
          tenantName: string,
          firstName: string,
          avatarBackgroundColor: string,
          routeToRedirectAfterAccountSwitch?: string
        ): void => {
          const activeAccount = {
            PersonId: personId,
            TenantName: tenantName,
            FirstName: firstName,
            backgroundColor: avatarBackgroundColor,
          };

          patchState(state, {
            activeAccount,
            routeToRedirectAfterAccountSwitch,
          });
          localStorage.setItem('activeAccount', JSON.stringify(activeAccount));
          authStore.changePersonAccount({ PersonId: personId });
        },
      };
    }
  ),
  withMethods(
    (
      state,
      accountService = inject(AccountService),
      userService = inject(UserService),
      personService = inject(PersonService),
      appConfig = inject(APP_CONFIG)
    ) => {
      /**
       * Helper method to ensure userProfile exists before sending email
       * Attempts to create userProfile with minimal information
       * Always includes PersonId when available to link to existing Person in different tenants
       * Always includes FirstName when available to update the user/client name
       * If firstName is not provided but PersonId is available, attempts to fetch firstName from existing user
       * Continues even if creation fails (e.g., user already exists in another tenant)
       * Always updates user with firstName after creation/retrieval if firstName was provided or fetched
       */
      const ensureUserProfileExists = (
        email: string,
        personId?: string,
        firstName?: string
      ): Observable<void> => {
        const fetchFirstNameIfNeeded = (): Observable<string> => {
          if (firstName) {
            return of(firstName);
          }
          if (personId) {
            return personService.getPerson(personId).pipe(
              map(
                (person) => person?.FirstName || email.split('@')[0] || 'User'
              ),
              catchError(() => {
                return userService.getUser(personId).pipe(
                  map(
                    (user) => user?.FirstName || email.split('@')[0] || 'User'
                  ),
                  catchError(() => of(email.split('@')[0] || 'User'))
                );
              })
            );
          }
          return of(email.split('@')[0] || 'User');
        };

        return fetchFirstNameIfNeeded().pipe(
          switchMap((firstNameToUse) => {
            const minimalUser: IUser = {
              PersonId: personId,
              Email: email,
              FirstName: firstNameToUse,
              RoleId: null,
              IsActive: true,
              IsOwner: false,
              AppSource: window['isFacilFit']
                ? AppSource.FacilFit
                : AppSource.Actuar,
              PasswordDefinitionMode: PasswordDefinitionMode.None,
            };

            return userService.addUser(minimalUser).pipe(
              switchMap((createdUser) => {
                if (createdUser && createdUser.FirstName !== firstNameToUse) {
                  // Use POST instead of PUT for updates
                  return userService
                    .addUser({
                      ...createdUser,
                      FirstName: firstNameToUse,
                      PasswordDefinitionMode:
                        createdUser.PasswordDefinitionMode ??
                        PasswordDefinitionMode.None,
                    })
                    .pipe(
                      map(() => undefined),
                      catchError(() => of(undefined))
                    );
                }
                return of(undefined);
              }),
              catchError(() => {
                if (personId) {
                  return userService.getUser(personId).pipe(
                    switchMap((existingUser) => {
                      if (
                        existingUser &&
                        existingUser.FirstName !== firstNameToUse
                      ) {
                        // Use POST instead of PUT for updates
                        return userService
                          .addUser({
                            ...existingUser,
                            FirstName: firstNameToUse,
                            PasswordDefinitionMode:
                              existingUser.PasswordDefinitionMode ??
                              PasswordDefinitionMode.None,
                          })
                          .pipe(
                            map(() => undefined),
                            catchError(() => of(undefined))
                          );
                      }
                      return of(undefined);
                    }),
                    catchError(() => of(undefined))
                  );
                }
                return of(undefined);
              })
            );
          })
        );
      };

      return {
        sendEmailForgotPassword: rxMethod<ISendEmailResetPassword>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('sendEmailForgotPassword'),
              })
            ),
            switchMap((email) => {
              // Password reset flow doesn't need to go through user profile update
              return accountService.sendEmailResetPassword(email).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, {
                      emailForgot: email.Email,
                      ...setPropLoaded('sendEmailForgotPassword'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('sendEmailForgotPassword', error),
                    });
                  },
                })
              );
            })
          )
        ),
        resendEmailForgotPassword: rxMethod<IResendEmailAccountConfirmation>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('resendEmailForgotPassword'),
              })
            ),
            switchMap((payload) => {
              // Password reset flow doesn't need to go through user profile update
              return accountService.resendEmailCodeConfirmAccount(payload).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, {
                      ...setPropLoaded('resendEmailForgotPassword'),
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('resendEmailForgotPassword', error),
                    });
                  },
                })
              );
            })
          )
        ),
        resendEmailCodeConfirmAccount:
          rxMethod<IResendEmailAccountConfirmation>(
            pipe(
              tap(() =>
                patchState(state, {
                  ...setPropLoading('resendEmailCodeConfirmAccount'),
                })
              ),
              switchMap((payload) => {
                return ensureUserProfileExists(payload.Email).pipe(
                  switchMap(() =>
                    accountService.resendEmailCodeConfirmAccount(payload).pipe(
                      tapResponse({
                        next: () => {
                          patchState(state, {
                            ...setPropLoaded('resendEmailCodeConfirmAccount'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          patchState(state, {
                            ...setPropError(
                              'resendEmailCodeConfirmAccount',
                              error
                            ),
                          });
                        },
                      })
                    )
                  )
                );
              })
            )
          ),

        resendEmailLinkConfirmation: rxMethod<{
          email: string;
          firstName?: string;
          personId?: string;
        }>(
          pipe(
            tap(() =>
              patchState(state, {
                ...setPropLoading('resendEmailLinkConfirmation'),
              })
            ),
            switchMap((payload) => {
              return ensureUserProfileExists(
                payload.email,
                payload.personId,
                payload.firstName
              ).pipe(
                switchMap(() =>
                  accountService
                    .resendEmailLinkConfirmation(payload.email)
                    .pipe(
                      tapResponse({
                        next: () => {
                          patchState(state, {
                            ...setPropLoaded('resendEmailLinkConfirmation'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          patchState(state, {
                            ...setPropError(
                              'resendEmailLinkConfirmation',
                              error
                            ),
                          });
                        },
                      })
                    )
                )
              );
            })
          )
        ),
        favoriteMenus: rxMethod<IFavoriteMenu[]>(
          pipe(
            tap(() =>
              patchState(state, { ...setPropLoading('favoriteMenus') })
            ),
            switchMap((payload) =>
              accountService.favoriteMenus(payload).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, { ...setPropLoaded('favoriteMenus') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, {
                      ...setPropError('favoriteMenus', error),
                    });
                  },
                })
              )
            )
          )
        ),
      };
    }
  ),
  withReqState(
    'createAccount',
    'resendCode',
    'resendEmailCodeConfirmAccount',
    'resendEmailForgotPassword',
    'checkResetPasswordCode',
    'checkAccountStatus',
    'confirmEmailAccount',
    'createAccountPassword',
    'sendEmailForgotPassword',
    'resetPassword',
    'resendEmailLinkConfirmation',
    'getOperationsPeriodsDescriptions',
    'checkCountryCodePhoneNumber',
    'confirmEmailAndCreatePassword',
    'favoriteMenus',
    'changeTemporaryPassword'
  )
);
